import {
  kaderdeurDoorPositions,
  kaderdeurSelectableHeights,
  kaderdeurSelectableWidths,
  useConfigurationOptionsContext,
} from "app/providers/DoorOptionsProvider";
import { SizeObject } from "app/providers/DoorOptionsProvider.types";
import { ChangeEvent, useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { defaultDoorWidth } from "shared/utils/doorSceneConstants";
import {
  setSelfAssemblyHeightInput,
  setSelfAssemblyWidthInput,
  setSelfAssemblyWidthInputs,
} from "shared/store/selfAssembly/selfAssemblyInputs";
import FormGroup from "shared/ui/FormGroup";
import Input from "shared/ui/input";
import Label from "shared/ui/label";
import { isNewLayoutCode } from "entities/configurator/model/layoutMeta";
import { applyHeightToEigenwijs } from "app/providers/eigenwijsHeight";
import { requiresEqualWidths } from "entities/configurator/model/widthSymmetry";
import {
  defaultMinDoorWidth,
  defaultMinPanelWidth,
  getStandingLayoutMinWidth,
  newLayoutMinWidth,
} from "entities/configurator/model/widthLimits";
import ToggleButton from "../../../widgets/shared/toggle/components/ToggleButton";
import { ToggleLayout } from "../../../widgets/shared/toggle/components/ToggleLayout";
import { KaderdeurSelfMontageHeight } from "./components/KaderdeurSelfMontageHeight";
import { KaderdeurSelfMontageWidths } from "./components/KaderdeurSelfMontageWidths";
import { OpdekdeurSelfMontageWidths } from "./components/OpdekdeurSelfMontageWidths";

const doorConfigsToFlip = [
  "taatsdeur1paneel",
  "scharnierdeur1paneel",
  "dubbel1paneel",
  "dubbeleschuifdeur1paneel",
];

const MeasurementOptionControls = () => {
  const {
    maxDoorWidth,
    minDoorHeight,
    maxDoorHeight,
    eigenwijs,
    layout,
    selectedDoor,
    updateEigenwijs,
    markPendingHeightRepairNotification,
    selfAssembleDoorData,
    isNewLayout,
    updateSearchParam,
    staanderPosition,
  } = useConfigurationOptionsContext();

  const [height, setHeight] = useState<number>(0);
  const [heightFormInput, setHeightFormInput] = useState<string>("");
  const [widths, setWidths] = useState<number[]>([]);
  const [widthFormInput, setWidthFormInput] = useState<string[]>([]);
  const heightFormInputInt: number = parseInt(heightFormInput);
  const equalWidthsRequired = requiresEqualWidths(
    selectedDoor?.code,
    layout?.code,
    staanderPosition !== undefined,
  );
  const standingMinimumWidth = selectedDoor
    ? getStandingLayoutMinWidth(
        selectedDoor.code,
        staanderPosition !== undefined,
      )
    : undefined;
  const minElementsWidths: number[] = [];
  selectedDoor?.configuration.elements?.forEach((element) => {
    if (standingMinimumWidth !== undefined) {
      if (element.type !== "measurement") {
        minElementsWidths.push(standingMinimumWidth);
      }
    } else if (isNewLayout) {
      minElementsWidths.push(newLayoutMinWidth);
    } else if (element.type === "paneel") {
      minElementsWidths.push(defaultMinPanelWidth);
    } else if (element.type !== "" && element.type !== "measurement") {
      minElementsWidths.push(defaultMinDoorWidth);
    }
  });
  if (selectedDoor && doorConfigsToFlip.includes(selectedDoor.code)) {
    minElementsWidths.reverse();
  }

  // Kaderdeur options
  const [kaderHasStandardWidths, setKaderHasStandardWidths] =
    useState<boolean>(true);
  const [kaderHasStandardHeights, setKaderHasStandardHeights] =
    useState<boolean>(false);

  const selectStandardWidthOption = () => {
    setKaderHasStandardWidths(true);
    toast.info(
      "Let op: hierbij wordt de deurhoogte aangegeven. Het kozijn wordt hierop aangepast.",
    );
  };

  const selectPlafondHeights = () => {
    setKaderHasStandardHeights(false);
  };

  useEffect(() => {
    let allWidths: number[] = [];

    let widthInputs: string[] = [];

    if (
      eigenwijs &&
      (eigenwijs.sizeObjects.length !== widths.length ||
        eigenwijs.sizeObjects.some(
          (sizeObject, index) => sizeObject.customWidth !== widths[index],
        ))
    ) {
      eigenwijs.sizeObjects.forEach((sizeObject) => {
        allWidths.push(sizeObject.customWidth);

        const width = selfAssembleDoorData.selfAssembly
          ? ""
          : sizeObject.customWidth.toString();

        widthInputs.push(width);
      });

      setWidths(allWidths);

      setWidthFormInput(widthInputs);
    }
  }, [eigenwijs, eigenwijs?.sizeObjects]);

  useEffect(() => {
    if (!selectedDoor || !eigenwijs || !eigenwijs.sizeObjects) return;

    if (height === 0 || height < minDoorHeight || height > maxDoorHeight) {
      setHeight(eigenwijs.sizeObjects[0].customHeight);

      const height = eigenwijs.sizeObjects[0].customHeight.toString();
      setHeightFormInput(height);
      setSelfAssemblyHeightInput(height);
      if (
        selfAssembleDoorData.selfAssembly &&
        kaderdeurSelectableHeights.includes(parseInt(height))
      ) {
        setKaderHasStandardHeights(true);
      }
    }

    if (widths.length !== selectedDoor?.configuration.nElements) {
      let allWidths: number[] = [];
      let widthInputs: string[] = [];

      if (
        eigenwijs.sizeObjects.length !== selectedDoor.configuration.nElements
      ) {
        let elements = selectedDoor.configuration.nElements || 0;
        for (let i = 0; i < elements; i++) {
          allWidths.push(defaultDoorWidth);
          widthInputs.push(defaultDoorWidth.toString());
        }
      } else {
        eigenwijs.sizeObjects.forEach((sizeObject, i) => {
          allWidths.push(sizeObject.customWidth);
          widthInputs.push(sizeObject.customWidth.toString());
        });
      }

      setWidths(allWidths);
      setWidthFormInput(widthInputs);
      setSelfAssemblyWidthInputs(widthInputs);
      const hasAllStandardWidths = allWidths.every((num) =>
        kaderdeurSelectableWidths.includes(num),
      );
      if (selfAssembleDoorData.selfAssembly && !hasAllStandardWidths) {
        setKaderHasStandardWidths(false);
      }
    }
  }, [eigenwijs]);

  const updatedHeight = (e: ChangeEvent<HTMLInputElement>) => {
    const newHeightValue = Number(e.target.value);

    setHeightFormInput(e.target.value);

    if (selfAssembleDoorData.selfAssembly) {
      setSelfAssemblyHeightInput(e.target.value);
    }

    if (newHeightValue >= minDoorHeight && newHeightValue <= maxDoorHeight) {
      commitHeight(newHeightValue);
    }
  };

  const handleHeightBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const newHeightValue = Number(e.target.value);

    if (
      isNaN(newHeightValue) ||
      newHeightValue < minDoorHeight ||
      newHeightValue > maxDoorHeight
    ) {
      // New modified height value
      let modifiedHeight = minDoorHeight;

      if (!eigenwijs || !eigenwijs.sizeObjects) {
        setHeightFormInput(modifiedHeight.toString());
        setHeight(modifiedHeight);
      } else {
        modifiedHeight = isOpdekdeurSelfMontage
          ? 2360
          : eigenwijs.sizeObjects[0].customHeight;

        setHeight(modifiedHeight);
        setHeightFormInput(modifiedHeight.toString());
      }
      commitHeight(modifiedHeight);
      toast.info("We hebben de hoogte opnieuw ingesteld.");
      if (selfAssembleDoorData.selfAssembly) {
        setSelfAssemblyHeightInput(modifiedHeight.toString());
      }
    }
  };

  // Commit height changes - set state variable + set source of truth (eigenwijs)
  const commitHeight = (newHeightValue: number) => {
    setHeight(newHeightValue);
    if (eigenwijs) {
      const newEigenwijsOptions = applyHeightToEigenwijs(
        eigenwijs,
        newHeightValue,
        layout?.code,
      );
      markPendingHeightRepairNotification();
      updateEigenwijs(newEigenwijsOptions);
    }
  };

  // Commit widtdh changes - set state variable + set source of truth (eigenwijs)
  const commitWidth = (allWidths: number[]) => {
    setWidths(allWidths);

    if (eigenwijs) {
      const newEigenwijsOptions = {
        ...eigenwijs,
        sizeObjects: allWidths.map((width, index): SizeObject => {
          return {
            customHeight: height,
            customWidth: width,
          };
        }),
      };

      updateEigenwijs(newEigenwijsOptions);
    }
  };

  const updatedWidth = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const newWidthValueString: string = e.target.value;
    const newWidthValue: number = parseInt(newWidthValueString);

    let allWidths = [...widths];
    let allWidthsInputs = [...widthFormInput];

    allWidths[index] = newWidthValue;
    allWidthsInputs[index] = newWidthValueString;

    if (equalWidthsRequired) {
      allWidths = allWidths.map(() => newWidthValue);
      allWidthsInputs = allWidthsInputs.map(() => newWidthValueString);
    }

    setWidthFormInput(allWidthsInputs);

    if (selfAssembleDoorData.selfAssembly) {
      setSelfAssemblyWidthInput(newWidthValueString, index);
    }

    if (
      newWidthValue >= minElementsWidths[index] &&
      newWidthValue <= maxDoorWidth
    ) {
      commitWidth(allWidths);
    }
  };

  const setWidth = useCallback(
    (width: number, index = 0) => {
      let allWidths = [...widths];
      allWidths[index] = width;
      if (equalWidthsRequired) allWidths = allWidths.map(() => width);

      let widthInputs = allWidths.map((width: number) => width.toString());

      setWidths(allWidths);
      setWidthFormInput(widthInputs);

      setSelfAssemblyWidthInput(width.toString(), index);
      commitWidth(allWidths);
    },
    [
      widths,
      widthFormInput,
      selfAssembleDoorData.selfAssembly,
      height,
      eigenwijs,
      equalWidthsRequired,
    ],
  );

  const setNewHeight = (height: number) => {
    setHeight(height);
    setHeightFormInput(height.toString());

    setSelfAssemblyHeightInput(height.toString());
    commitHeight(height);
  };

  const handleWidthBlur = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const newWidthValueString: string = e.target.value;
    const newWidthValue: number = parseInt(newWidthValueString);

    if (
      isNaN(newWidthValue) ||
      newWidthValue < minElementsWidths[index] ||
      newWidthValue > maxDoorWidth
    ) {
      let allWidths = [...widths];
      let allWidthsInputs = [...widthFormInput];

      allWidths[index] = defaultDoorWidth;
      allWidthsInputs[index] = defaultDoorWidth.toString();

      if (equalWidthsRequired) {
        allWidths = allWidths.map(() => defaultDoorWidth);
        allWidthsInputs = allWidthsInputs.map(() =>
          defaultDoorWidth.toString(),
        );
      }

      toast.info("We hebben de breedte opnieuw ingesteld.");
      setWidthFormInput(allWidthsInputs);
      commitWidth(allWidths);

      if (selfAssembleDoorData.selfAssembly) {
        setSelfAssemblyWidthInput(allWidths[index].toString(), index);
      }
    }
  };

  const handleValidationTypeHeight = (value: number): string => {
    if (value < minDoorHeight) {
      return "Deur is te laag, minimum deurhoogte is: " + minDoorHeight + " mm";
    } else if (value > maxDoorHeight) {
      return "Deur is te hoog, maximum deurhoogte is: " + maxDoorHeight + " mm";
    }

    return "";
  };

  const handleValidationTypeWidth = (value: number, index: number): string => {
    if (value < minElementsWidths[index]) {
      return (
        "Deur is te smal, minimum deurbreedte is: " +
        minElementsWidths[index] +
        " mm"
      );
    } else if (value > maxDoorWidth) {
      return (
        "Deur is te breed, maximum deurbreedte is: " + maxDoorWidth + " mm"
      );
    }
    return "";
  };

  const getErrorMessage = (
    type: string,
    value: number,
    index: number = -1,
  ): string => {
    if (type === "height") {
      return handleValidationTypeHeight(value);
    } else if (type === "width") {
      return handleValidationTypeWidth(value, index);
    }
    return "";
  };

  if (!selectedDoor) return null;

  // Determine if the door is self-assemble and the door type
  const isOpdekdeurSelfMontage =
    selfAssembleDoorData.selfAssembly && selectedDoor.code === "opdekdeur";

  // Is the door a taatsdeur and self-assemble
  const isTaatsdeurSelfMontage =
    selfAssembleDoorData.selfAssembly && selectedDoor.code === "taatsdeur";

  // Is the door a schuifdeur and self-assemble
  const isSchuifdeurSelfMontage =
    selfAssembleDoorData.selfAssembly && selectedDoor.code === "schuifdeur";

  // Is the door a schuifdeur or taatsdeur and self-assemble
  const isSchuifTaatsSelfMontage =
    selfAssembleDoorData.selfAssembly &&
    (selectedDoor.code === "taatsdeur" || selectedDoor.code === "schuifdeur");

  // Is the door a kaderdeur and self-assemble
  const isKaderSelfMontage =
    selfAssembleDoorData.selfAssembly &&
    (selectedDoor.code === "kaderdeur" ||
      selectedDoor.code === "dubbelekaderdeur" ||
      selectedDoor.code === "zakelijkkozijn");

  // Is the door a dubbelekaderdeur and self-assemble
  const isKaderDouble =
    selfAssembleDoorData.selfAssembly &&
    selectedDoor.code === "dubbelekaderdeur";

  // Determine the text for the width label
  const widthText = isSchuifTaatsSelfMontage
    ? "Sparingsbreedte"
    : isOpdekdeurSelfMontage
      ? "Sponningsbreedte (mm)"
      : isKaderSelfMontage
        ? "Deurbreedte"
        : "Breedte";

  const heightText = isSchuifTaatsSelfMontage
    ? "Sparingshoogte"
    : isOpdekdeurSelfMontage
      ? "Sponningshoogte"
      : isKaderSelfMontage
        ? "Hoogte inclusief kozijn"
        : "Hoogte";

  const shouldDisableInput = (index: number): boolean => {
    return equalWidthsRequired && index > 0;
  };

  return (
    <div className="gg-grid gg-grid-cols-12 gg-gap-5">
      <FormGroup className="gg-col-span-12">
        <Label htmlFor="name" className="gg-mb-2 gg-text-sm gg-font-bold">
          {widthText}
        </Label>

        {isSchuifTaatsSelfMontage && isTaatsdeurSelfMontage ? (
          <h3 className="gg-mb-2 gg-text-sm">
            Meet de strakke maten van wand tot wand in. Hierbij hoef je geen
            rekening te houden met spelingsruimte voor het draaien van de deur.
            Meet op 3 punten in en vermeld hieronder de{" "}
            <span className="gg-font-bold gg-italic gg-underline">smalste</span>{" "}
            maat.
          </h3>
        ) : (
          isSchuifdeurSelfMontage && (
            <h3 className="gg-mb-2 gg-text-sm">
              Meet de strakke maten van wand tot wand in. Een eventuele overlap
              wordt verderop berekend.
            </h3>
          )
        )}
        {isOpdekdeurSelfMontage ? (
          <OpdekdeurSelfMontageWidths
            selectedWidth={widths[0]}
            setWidth={setWidth}
            minimumWidth={standingMinimumWidth ?? defaultMinDoorWidth}
          />
        ) : isKaderSelfMontage ? (
          <div>
            <ToggleLayout>
              <ToggleButton
                isActive={kaderHasStandardWidths}
                onClick={selectStandardWidthOption}
                roundedClass="gg-rounded-l-sm gg-border-l gg-grid gg-place-items-center "
                layoutId="kaderWidthToggle"
                dataTestId="kaderWidthToggleStandard"
              >
                Standaard breedte
              </ToggleButton>

              <ToggleButton
                isActive={!kaderHasStandardWidths}
                onClick={() => setKaderHasStandardWidths(false)}
                roundedClass="gg-rounded-r-sm gg-border-r gg-grid gg-place-items-center "
                layoutId="kaderWidthToggle"
                dataTestId="kaderWidthToggleOther"
              >
                Eigen breedtemaat
              </ToggleButton>
            </ToggleLayout>
            <div className="gg-mt-4 gg-flex gg-w-full gg-flex-col">
              {kaderHasStandardWidths ? (
                <KaderdeurSelfMontageWidths
                  totalDoors={isKaderDouble ? 2 : 1}
                  selectedWidths={widths}
                  setWidth={setWidth}
                />
              ) : (
                <div className="gg-flex gg-w-full gg-flex-col gg-space-y-4">
                  {widthFormInput.map((width, index) => (
                    <div key={index}>
                      {isKaderDouble && (
                        <Label
                          htmlFor="name"
                          className="gg-mb-2 gg-text-sm gg-font-bold"
                        >
                          {kaderdeurDoorPositions[index]}
                        </Label>
                      )}
                      <Input
                        key={`kader-width-${index}`}
                        className={"gg-border-dark-blue-200 gg-py-3 gg-pr-12"}
                        id={`width-form-input-${index}`}
                        data-testid={"door-width"}
                        type={"number"}
                        placeholder="Breedte"
                        value={width}
                        onChange={(e) => updatedWidth(e, index)}
                        onBlur={(e) => handleWidthBlur(e, index)}
                        suffix={"mm"}
                        error={
                          parseInt(widthFormInput[index]) <
                            minElementsWidths[index] ||
                          parseInt(widthFormInput[index]) > maxDoorWidth ||
                          isNaN(parseInt(widthFormInput[index]))
                        }
                        errorText={getErrorMessage(
                          "width",
                          parseInt(widthFormInput[index]),
                          index,
                        )}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="gg-flex gg-w-full gg-flex-col gg-space-y-4">
            {widthFormInput.map((width, index) => (
              <Input
                readOnly={shouldDisableInput(index)}
                key={`width-${index}`}
                className={"gg-border-dark-blue-200 gg-py-3 gg-pr-12"}
                id={`width-form-input-${index}`}
                data-testid={"door-width"}
                type={"number"}
                placeholder="Breedte"
                value={width}
                onChange={(e) => updatedWidth(e, index)}
                onBlur={(e) => handleWidthBlur(e, index)}
                suffix={"mm"}
                error={
                  parseInt(widthFormInput[index]) < minElementsWidths[index] ||
                  parseInt(widthFormInput[index]) > maxDoorWidth ||
                  isNaN(parseInt(widthFormInput[index]))
                }
                errorText={getErrorMessage(
                  "width",
                  parseInt(widthFormInput[index]),
                  index,
                )}
              />
            ))}
          </div>
        )}
      </FormGroup>
      <FormGroup className="gg-col-span-12">
        <Label htmlFor="name" className="gg-mb-2 gg-text-sm gg-font-bold">
          {heightText}
        </Label>

        {isSchuifTaatsSelfMontage && isTaatsdeurSelfMontage ? (
          <h3 className="gg-mb-2 gg-text-sm">
            Meet de strakke maten van vloer tot bovenzijde sparing/plafond in.
            Hierbij hoef je geen rekening te houden met spelingsruimte voor het
            draaien van de deur. Meet op 3 punten in en vermeld hieronder de
            <span className="gg-font-bold gg-italic gg-underline">
              kortste
            </span>{" "}
            maat.
          </h3>
        ) : (
          isSchuifdeurSelfMontage && (
            <h3 className="gg-mb-2 gg-text-sm">
              Meet de strakke maten van vloer tot bovenzijde sparing/plafond in.
              Wij houden rekening met de rails en onderspeling.
            </h3>
          )
        )}

        {isKaderSelfMontage ? (
          <div>
            <ToggleLayout>
              <ToggleButton
                isActive={kaderHasStandardHeights}
                onClick={() => setKaderHasStandardHeights(true)}
                roundedClass="gg-rounded-r-sm gg-border-r gg-grid gg-place-items-center "
                layoutId="kaderHeightToggle"
                dataTestId="kaderHeightToggleStandard"
              >
                Standaard hoogte
              </ToggleButton>
              <ToggleButton
                isActive={!kaderHasStandardHeights}
                onClick={selectPlafondHeights}
                roundedClass="gg-rounded-l-sm gg-border-l gg-grid gg-place-items-center "
                layoutId="kaderHeightToggle"
                dataTestId="kaderHeightToggleOther"
              >
                Eigen hoogtemaat
              </ToggleButton>
            </ToggleLayout>
            <div className="gg-mt-4 gg-flex gg-w-full gg-flex-col">
              {kaderHasStandardHeights ? (
                <KaderdeurSelfMontageHeight
                  selectedHeight={height}
                  setHeight={setNewHeight}
                />
              ) : (
                <div className="gg-flex gg-w-full gg-flex-col gg-space-y-4">
                  <Input
                    id="height-form-input"
                    data-testid={"door-height"}
                    className={"gg-border-dark-blue-200 gg-py-3 gg-pr-12"}
                    type={"number"}
                    placeholder="Hoogte"
                    value={heightFormInput}
                    onChange={updatedHeight}
                    onBlur={handleHeightBlur}
                    suffix={"mm"}
                    error={
                      heightFormInputInt < minDoorHeight ||
                      heightFormInputInt > maxDoorHeight ||
                      isNaN(heightFormInputInt)
                    }
                    errorText={getErrorMessage("height", heightFormInputInt)}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <Input
            id="height-form-input"
            data-testid={"door-height"}
            className={"gg-border-dark-blue-200 gg-py-3 gg-pr-12"}
            type={"number"}
            placeholder="Hoogte"
            value={heightFormInput}
            onChange={updatedHeight}
            onBlur={handleHeightBlur}
            suffix={"mm"}
            error={
              heightFormInputInt < minDoorHeight ||
              heightFormInputInt > maxDoorHeight ||
              isNaN(heightFormInputInt)
            }
            errorText={getErrorMessage("height", heightFormInputInt)}
          />
        )}
      </FormGroup>
    </div>
  );
};
export default MeasurementOptionControls;
